import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Radio } from "lucide-react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";

const MD_COMPONENTS = {
  p: (props) => <p className="mb-3 last:mb-0" {...props} />,
  strong: (props) => <strong className="text-zinc-100 font-semibold" {...props} />,
  em: (props) => <em className="text-zinc-300" {...props} />,
  a: (props) => <a className="text-nc-blue hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
  ul: (props) => <ul className="mb-3 flex flex-col gap-1.5" {...props} />,
  ol: (props) => <ol className="mb-3 flex flex-col gap-1.5 list-decimal list-inside" {...props} />,
  li: (props) => <li className="pl-4 relative before:content-['//'] before:absolute before:left-0 before:text-nc-orange" {...props} />,
  h1: (props) => <h1 className="font-sans font-bold uppercase text-xl text-zinc-100 mt-4 mb-2" {...props} />,
  h2: (props) => <h2 className="font-sans font-bold uppercase text-lg text-zinc-100 mt-4 mb-2" {...props} />,
  h3: (props) => <h3 className="font-sans font-semibold uppercase text-base text-zinc-100 mt-3 mb-2" {...props} />,
  code: (props) => <code className="bg-white/10 text-nc-orange px-1.5 py-0.5 text-[11px]" {...props} />,
  blockquote: (props) => <blockquote className="border-l-2 border-nc-orange/50 pl-4 text-zinc-500 my-3" {...props} />,
};

export default function Devlog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/devlog?category=new-citizen").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  if (!posts) return null;

  return (
    <section data-testid="devlog-section" className="mt-4 border border-white/10 bg-black/40 backdrop-blur-md relative overflow-hidden">
      <div className="absolute inset-0 grid-texture opacity-40" />
      <div className="relative p-6 md:p-10">
        <div className="flex items-center gap-4 mb-8">
          <Radio className="w-5 h-5 text-nc-orange" />
          <h3 data-testid="devlog-title" className="font-sans font-semibold uppercase tracking-[0.3em] text-lg md:text-xl text-zinc-100">
            Devlog // Transmissions
          </h3>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {posts.length === 0 ? (
          <p data-testid="devlog-empty" className="font-mono text-xs tracking-[0.25em] text-zinc-600 uppercase">
            No transmissions yet. Stand by.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {posts.map((post, i) => (
              <motion.article
                key={post.id}
                data-testid={`devlog-post-${i + 1}`}
                className="border-l-2 border-nc-blue/60 bg-[#050505]/60 p-5 hover:border-nc-blue transition-colors duration-300"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <h4 data-testid={`devlog-post-${i + 1}-title`} className="font-sans font-bold uppercase text-lg text-zinc-100">
                    {post.title}
                  </h4>
                  <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
                    {String(post.createdAt).slice(0, 10)}
                  </span>
                </div>
                {post.image && (
                  <img
                    data-testid={`devlog-post-${i + 1}-image`}
                    src={post.image}
                    alt={post.title}
                    loading="lazy"
                    className="w-full max-h-96 object-cover border border-white/10 mt-4"
                  />
                )}
                <div className="font-mono text-xs text-zinc-400 leading-relaxed mt-3">
                  <ReactMarkdown components={MD_COMPONENTS}>{post.body}</ReactMarkdown>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
